import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Filter, UserCheck, UserX, RefreshCw, Plus, Download, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '../hooks/useAuth';
import { 
  getAllRSVPEntries, 
  getAllRSVPEntriesFromFirebase,
  getRSVPStats, 
  filterRSVPEntries, 
  addSampleData, 
  exportToCSV, 
  syncWithGoogleSheets,
  RSVPEntry 
} from '../services/rsvpService';

// Importar el servicio de Firebase para escuchar cambios en tiempo real
import { listenToRSVPChanges } from '../services/firebaseService';

// Versión corregida y mejorada del Dashboard RSVP
const Dashboard: React.FC = () => {
  // Estados para manejar los datos y la UI
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rsvpData, setRsvpData] = useState<RSVPEntry[]>([]);
  const [filteredData, setFilteredData] = useState<RSVPEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [error, setError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<'success' | 'failed' | 'pending'>('pending');
  
  const { toast } = useToast();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  
  // Referencia para controlar si estamos en el montaje inicial
  const isInitialMount = useRef(true);

  // Proteger la ruta del dashboard
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Cargar datos del RSVP priorizando Firebase como fuente principal
  const fetchRSVPData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Iniciando carga de datos RSVP desde Firebase...');
      
      // Usar la función mejorada que prioriza Firebase
      const data = await getAllRSVPEntriesFromFirebase();
      
      console.log(`Datos RSVP cargados: ${data.length} registros`);
      
      // Actualizar el estado con los datos obtenidos
      setRsvpData(data);
      setFilteredData(data);
      
      // Si no hay datos después de intentar cargar desde Firebase, intentar cargar datos de ejemplo
      if (data.length === 0) {
        console.log('No hay datos RSVP, intentando cargar datos de ejemplo...');
        
        // Eliminar el indicador de datos de ejemplo para permitir recargarlos
        localStorage.removeItem('sample_data_added');
        
        // Añadir datos de ejemplo
        const sampleSuccess = addSampleData();
        
        if (sampleSuccess) {
          // Marcar que se han añadido datos de ejemplo
          localStorage.setItem('sample_data_added', 'true');
          
          // Volver a cargar los datos
          const updatedData = getAllRSVPEntries();
          setRsvpData(updatedData);
          setFilteredData(updatedData);
          
          toast({
            title: 'Datos de ejemplo cargados',
            description: 'Se han añadido datos de ejemplo al dashboard.',
            variant: 'default',
          });
        } else {
          setError('No se pudieron cargar datos. Por favor, contacta al administrador.');
          toast({
            title: 'Error',
            description: 'No se pudieron cargar datos de ejemplo.',
            variant: 'destructive',
          });
        }
      }
      
      // Establecer estado de sincronización basado en los datos disponibles
      setSyncStatus(data.length > 0 ? 'success' : 'failed');
      
    } catch (error) {
      console.error('Error al cargar datos RSVP:', error);
      setSyncStatus('failed');
      setError('Ocurrió un error al cargar los datos. Intenta refrescar la página.');
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos del RSVP. Por favor, intenta nuevamente.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    if (isAuthenticated) {
      fetchRSVPData();
    }
  }, [isAuthenticated]);
  
  // Configurar escucha en tiempo real para actualizaciones de Firebase
  useEffect(() => {
    if (!isAuthenticated) return;
    
    console.log('Configurando escucha en tiempo real para actualizaciones RSVP...');
    
    // Configurar el listener para cambios en tiempo real
    const unsubscribe = listenToRSVPChanges((entries) => {
      console.log(`Datos RSVP actualizados en tiempo real: ${entries.length} registros`);
      
      // Actualizar el estado con los nuevos datos
      setRsvpData(entries);
      
      // Actualizar los datos en el estado primero
      setRsvpData(entries);
      
      // Aplicar filtros actuales
      const attendanceFilter = activeTab === 'all' ? 'all' : 
                             activeTab === 'attending' ? 'attending' : 'not-attending';
      
      // Usar filterRSVPEntries con los nuevos datos en el estado
      const filtered = filterRSVPEntries(searchTerm, attendanceFilter);
      setFilteredData(filtered);
      
      // Actualizar estado de sincronización
      setSyncStatus('success');
      
      // Notificar al usuario sobre la actualización
      if (!loading) {
        toast({
          title: 'Datos actualizados',
          description: 'Se han recibido nuevos datos RSVP en tiempo real.',
          variant: 'default',
        });
      }
    });
    
    // Limpiar el listener cuando se desmonte el componente
    return () => {
      console.log('Limpiando escucha en tiempo real...');
      unsubscribe();
    };
  }, [isAuthenticated, activeTab, searchTerm, loading]);

  // Función para refrescar los datos
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchRSVPData();
    setRefreshing(false);
    toast({
      title: 'Datos actualizados',
      description: 'Los datos del RSVP han sido actualizados correctamente.',
    });
  };
  
  // Función para exportar datos a CSV
  const handleExportCSV = () => {
    try {
      setExporting(true);
      
      // Obtener datos en formato CSV
      const csvData = exportToCSV();
      
      if (!csvData) {
        throw new Error('No hay datos para exportar');
      }
      
      // Crear un blob con los datos CSV
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      
      // Crear un enlace para descargar el archivo
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `rsvp_data_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      // Añadir el enlace al DOM, hacer clic y luego eliminarlo
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: 'Exportación exitosa',
        description: 'Los datos del RSVP han sido exportados correctamente.',
      });
    } catch (error) {
      console.error('Error al exportar datos:', error);
      toast({
        title: 'Error de exportación',
        description: error instanceof Error ? error.message : 'No se pudieron exportar los datos.',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  // Filtrar datos según la búsqueda y la pestaña activa usando el servicio
  useEffect(() => {
    // Mapear el activeTab a los valores esperados por el servicio
    const attendanceFilter = activeTab === 'all' ? 'all' : 
                             activeTab === 'attending' ? 'attending' : 'not-attending';
    
    // Usar el servicio para filtrar los datos
    const result = filterRSVPEntries(searchTerm, attendanceFilter);
    setFilteredData(result);
  }, [searchTerm, activeTab]);

  // Obtener estadísticas del servicio
  const stats = getRSVPStats();
  const totalResponses = stats.total;
  const totalAttending = stats.attending;
  const totalNotAttending = stats.notAttending;

  // Formatear fecha
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return 'Fecha no válida';
    }
  };

  if (!isAuthenticated) {
    return null; // No renderizar nada si no está autenticado
  }

  return (
    <div className="min-h-screen bg-sage/10 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-parisienne text-forest">Dashboard de RSVP</h1>
          <div className="flex gap-4">
            <Button 
              variant="outline" 
              onClick={handleRefresh} 
              disabled={refreshing}
              className="flex items-center gap-2"
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Actualizar
            </Button>
            <Button 
              variant="outline" 
              onClick={handleExportCSV} 
              disabled={exporting || rsvpData.length === 0}
              className="flex items-center gap-2"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Exportar CSV
            </Button>
            <Button 
              variant="outline" 
              onClick={async () => {
                const success = addSampleData();
                
                if (success) {
                  // Intentar sincronizar los nuevos datos con Google Sheets
                  try {
                    await syncWithGoogleSheets();
                  } catch (syncError) {
                    console.warn('No se pudieron sincronizar los datos de ejemplo:', syncError);
                  }
                }
                
                handleRefresh();
                
                toast({
                  title: success ? 'Datos de ejemplo añadidos' : 'Advertencia',
                  description: success 
                    ? 'Se han añadido datos de ejemplo al dashboard.' 
                    : 'Algunos datos de ejemplo no pudieron ser añadidos debido a problemas de almacenamiento.',
                  variant: success ? 'default' : 'destructive',
                });
              }}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Añadir ejemplos
            </Button>
            <Button 
              variant="ghost" 
              onClick={logout}
              className="text-forest hover:text-forest/80"
            >
              Cerrar sesión
            </Button>
          </div>
        </div>

        {/* Estado de sincronización */}
        {syncStatus === 'success' && (
          <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-3 mb-6 rounded text-sm">
            <div className="flex items-center">
              <span className="mr-2">✓</span>
              <p>Los datos están sincronizados y disponibles para todos los dispositivos.</p>
            </div>
          </div>
        )}
        {syncStatus === 'failed' && filteredData.length > 0 && (
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-3 mb-6 rounded text-sm">
            <div className="flex items-center">
              <span className="mr-2">⚠</span>
              <p>Los datos se muestran desde el almacenamiento local. La sincronización con el servidor falló.</p>
            </div>
          </div>
        )}

        {/* Tarjetas de estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-white/90 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-baskerville text-forest">Total de respuestas</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-forest">{totalResponses}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white/90 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-baskerville text-forest flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-green-600" />
                Asistirán
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-green-600">{totalAttending}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white/90 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-baskerville text-forest flex items-center gap-2">
                <UserX className="h-5 w-5 text-red-500" />
                No asistirán
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-red-500">{totalNotAttending}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filtros y búsqueda */}
        <div className="bg-white/90 rounded-lg p-6 mb-8 shadow-sm">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-6">
            <div className="flex items-center gap-2 w-full md:w-auto">
              <Filter className="h-5 w-5 text-forest" />
              <h2 className="text-xl font-baskerville text-forest">Filtros</h2>
            </div>
            <div className="w-full md:w-1/3">
              <Input
                placeholder="Buscar por nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-sage/60 focus:ring-forest/40"
              />
            </div>
          </div>

          <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-sage/20 mb-6">
              <TabsTrigger value="all">Todos</TabsTrigger>
              <TabsTrigger value="attending">Asistirán</TabsTrigger>
              <TabsTrigger value="not-attending">No asistirán</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Mensaje de error - Solo mostrar si no hay datos */}
        {error && filteredData.length === 0 && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-8 rounded" role="alert">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <p><strong>Problema detectado</strong></p>
            </div>
            <p>{error}</p>
            <p className="text-sm mt-2">Sugerencia: Intenta refrescar la página o borrar la caché del navegador si el problema persiste.</p>
          </div>
        )}
        
        {/* Tabla de datos */}
        <div className="bg-white/90 rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center p-12">
              <Loader2 className="h-8 w-8 animate-spin text-forest" />
              <span className="ml-2 text-forest">Cargando datos...</span>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center p-12 text-forest/70">
              No se encontraron registros que coincidan con los filtros.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-sage/20">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-baskerville text-forest">Nombre</th>
                    <th className="px-6 py-3 text-left text-sm font-baskerville text-forest">Asistencia</th>
                    <th className="px-6 py-3 text-left text-sm font-baskerville text-forest">Alergias/Preferencias</th>
                    <th className="px-6 py-3 text-left text-sm font-baskerville text-forest">Recomendaciones Musicales</th>
                    <th className="px-6 py-3 text-left text-sm font-baskerville text-forest">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sage/20">
                  {filteredData.map((entry) => (
                    <tr key={entry.id} className="hover:bg-sage/10">
                      <td className="px-6 py-4 text-sm text-forest">{entry.name}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          entry.attendance === 'Sí asistiré' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {entry.attendance}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-forest/80">{entry.allergies || '-'}</td>
                      <td className="px-6 py-4 text-sm text-forest/80">{entry.musicRecommendations || '-'}</td>
                      <td className="px-6 py-4 text-sm text-forest/80">{formatDate(entry.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
